import { type FC, useRef } from "react";

import * as analytics from "src/lib/analytics";
import { Alert } from "@hightouch/htui";
import { LinkButton, useLocation } from "src/router";
import { useUser } from "src/contexts/user-context";
import { useEntitlements } from "src/hooks/use-entitlement";
import { useExternalBucketsQuery } from "src/graphql";

export const OrganizationRequiresSensitiveDataControlsBanner: FC = () => {
  const analyticsAlreadyFired = useRef(false);
  const { workspace } = useUser();
  const organization = workspace?.organization;
  const { data: entitlementsData } = useEntitlements(false);
  const externalStorageEnabled = entitlementsData.entitlements.externalStorage;
  const { data: externalBucketsData } = useExternalBucketsQuery();
  const hasExternalBucket = Boolean(
    externalBucketsData?.external_buckets?.length,
  );
  const location = useLocation();

  // Don't show the banner if:
  //   - the workspace is not entitled to external storage,
  //   - the organization does not require sensitive data controls,
  //   - or if there is already an external bucket
  if (
    !externalStorageEnabled ||
    !organization?.requires_sensitive_data_controls ||
    hasExternalBucket
  ) {
    return null;
  }

  // Only show the banner on certain pages
  const allowedPaths = [
    "/home",
    "/syncs",
    "/models",
    "/sequences",
    "/audiences",
    "/journeys",
    "/destinations",
    "/sources",
    "/settings",
  ];
  const currentPath = location.pathname;
  const isAllowedPage = allowedPaths.some((path) => currentPath.includes(path));

  if (!isAllowedPage) {
    return null;
  }

  if (!analyticsAlreadyFired.current) {
    analyticsAlreadyFired.current = true;
    analytics.track(
      "Organization Requires Sensitive Data Controls Banner Shown",
      {
        workspace_id: workspace?.id,
        organization_id: organization?.id,
      },
    );
  }

  return (
    <Alert
      variant="banner"
      type="warning"
      title="External storage bucket required."
      message={[
        "Your organization's data privacy settings require an external storage bucket to be configured.",
        workspace?.external_bucket_required_at
          ? `Syncs may fail after ${new Date(workspace?.external_bucket_required_at).toLocaleString()}.`
          : "",
      ].join(" ")}
      actions={
        <LinkButton
          href="/settings/storage"
          onClick={() => {
            analytics.track(
              "Organization Requires Sensitive Data Controls Banner Storage Settings Clicked",
              {
                workspace_id: workspace?.id,
                organization_id: organization?.id,
              },
            );
          }}
        >
          Add storage bucket
        </LinkButton>
      }
    />
  );
};
